import { PoolToken } from '@trustpad/launchpad';
import {
  dayjsLocalizer,
  generateDistributionAndListingEvents,
  generateSaleEvents,
  CustomEvent,
} from '@trustpad/launchpad/dist/calendar';

import Link from 'next/link';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { Calendar, EventWrapperProps, Views } from 'react-big-calendar';
import { Button, Modal } from 'react-bootstrap';
import { FaCoins, FaRocket } from 'react-icons/fa';

import { poolsConfig, poolsConfigById } from '~config/poolsConfig';
import {
  PoolProvider,
  usePool,
  useSaleTimeline,
  useUserPoolContribution,
} from '~features/pools';
import { DistributionTabs } from '~features/pools/components/Distribution';
import { Header } from '~features/pools/components/PoolSecondaryBlock/Header';
import { ListItem } from '~features/pools/components/PoolSecondaryBlock/InfoListItem';
import { ListingItem } from '~features/pools/components/PoolSecondaryBlock/ListingItem';
import { PoolConfig } from '~features/pools/types';

const localizer = dayjsLocalizer();

// TODO: pool search
export function ScheduleCalendar() {
  const events = useMemo(() => {
    return [
      ...generateSaleEvents(poolsConfig),
      ...generateDistributionAndListingEvents(
        poolsConfig.filter((p) => !p.sale?.refunded),
      ),
    ];
  }, []);
  const [selectedEvent, setSelectedEvent] = useState<CustomEvent>();

  return (
    <>
      {/* @ts-expect-error */}
      <Calendar<CustomEvent>
        popup={true}
        style={{ minHeight: 900 }}
        views={[Views.MONTH]}
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={(e) => setSelectedEvent(e)}
        components={{ eventWrapper: CustomEventTile }}
      />

      {selectedEvent && (
        <PoolProvider config={poolsConfigById[selectedEvent.poolId]}>
          {selectedEvent.type === 'ido' ? (
            <PoolInfoModal onClose={() => setSelectedEvent(undefined)} />
          ) : ['distribution', 'listing'].includes(selectedEvent.type) ? (
            <PoolDistributionModal
              onClose={() => setSelectedEvent(undefined)}
            />
          ) : null}
        </PoolProvider>
      )}
    </>
  );
}

function EventButton({
  token,
  onSelect,
  event,
  imgIcon,
}: {
  token: PoolToken;
  onSelect: (event: CustomEvent) => void;
  event: CustomEvent;
  imgIcon?: JSX.Element;
}) {
  return (
    <button
      className="bg-mainDark p-[2px] md:p-1 flex items-center hover:bg-main cursor-pointer w-full rounded text-xs md:text-sm flex-wrap"
      onClick={() => onSelect(event)}>
      <div className="relative flex items-center justify-center mr-2 flex-shrink-0">
        <img
          src={token.imageSrc}
          className="inline-block h-4 md:h-7 rounded"
          alt={token.name}
        />
        {imgIcon && (
          <>
            <div className="bg-black bg-opacity-70 rounded h-full w-full absolute" />
            {imgIcon}
          </>
        )}
      </div>
      <span className="">{token.symbol}</span>
    </button>
  );
}

const distrIcon = (
  <FaCoins className="inline-block absolute" style={{ fontSize: '10px' }} />
);

const listIcon = (
  <FaRocket className="inline-block absolute" style={{ fontSize: '10px' }} />
);

const icons = {
  listing: listIcon,
  distribution: distrIcon,
};

// @ts-expect-error
function CustomEventTile({ event, onSelect }: EventWrapperProps<CustomEvent>) {
  const pool = poolsConfigById[event.poolId];

  return (
    <EventButton
      token={pool.token}
      onSelect={onSelect}
      event={event}
      imgIcon={icons[event.type]}
    />
  );
}

function BaseModal({
  title,
  pool,
  onClose,
  children,
}: {
  title: string;
  pool: PoolConfig;
  onClose: VoidFunction;
  children: React.ReactNode;
}) {
  return (
    <Modal onHide={onClose} show>
      <Modal.Header className="pb-0" closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="space-y-6">
        <Header
          token={pool.token}
          description={pool.description}
          social={pool.social}
        />

        {children}
      </Modal.Body>
      <Modal.Footer>
        <Link href={`/pool/${pool.id}`} passHref legacyBehavior>
          <Button variant="main">Pool page</Button>
        </Link>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function PoolDistributionModal({ onClose }: { onClose: VoidFunction }) {
  const { pool } = usePool();
  const { poolBalance } = useUserPoolContribution();

  return (
    <BaseModal title="IDO on TrustPad" pool={pool} onClose={onClose}>
      <ListingItem />

      <DistributionTabs
        distribution={pool.distribution}
        token={pool.token}
        poolBalance={poolBalance}
      />
    </BaseModal>
  );
}

function PoolInfoModal({ onClose }: { onClose: VoidFunction }) {
  const { pool, isLoading } = usePool();
  const { start, end, registerStart, registerEnd, fcfsStart } =
    useSaleTimeline();

  return (
    <BaseModal title="IDO on TrustPad" pool={pool} onClose={onClose}>
      {isLoading ? null : (
        <ul>
          <ListItem label="Start">
            {start.format('D MMM, HH:mm a')} UTC
          </ListItem>
          {end && (
            <ListItem label="End">{end.format('D MMM, HH:mm a')} UTC</ListItem>
          )}

          {registerStart && (
            <>
              <ListItem label="Registration Start">
                {registerStart.format('D MMM, HH:mm a')} UTC
              </ListItem>
              <ListItem label="Registration End">
                {registerEnd.format('D MMM, HH:mm a')} UTC
              </ListItem>
            </>
          )}
          {fcfsStart && (
            <ListItem label="FCFS Opens">
              {fcfsStart.format('D MMM, HH:mm a')} UTC (
              {Math.round(pool.sale.fcfsDuration * 60)}m before the end)
            </ListItem>
          )}
        </ul>
      )}
    </BaseModal>
  );
}
